import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { env } from '@/config/env';
import { getSupabase } from '@/lib/supabase';
import { safeStorage } from '@/lib/storage';
import { useI18n } from '@/i18n/context';
import { parseAccessProfile, type AccessProfile } from '@/features/access/types';
import { linkAttributionSession } from '@/features/marketing/tracker';
import { AuthContext, type AuthStatus, type AuthValue, type SignUpInput } from './context';

/** User the stored language preference was last applied for (cleared on sign-out). */
const LANGUAGE_APPLIED_KEY = 'aqs.auth.languageAppliedFor';

/**
 * Session + authorization state.
 *
 * Roles/permissions are loaded from the database (get_my_access RPC) for the
 * signed-in user. They are used for routing and navigation only; the database
 * enforces them independently through RLS on every request.
 *
 * `getSupabase()` is async (the `@supabase/supabase-js` chunk is dynamically
 * imported — PERF-1, visual-experience-spec §J) so every use below awaits it
 * fresh rather than closing over a synchronously-obtained client. This keeps
 * the initial render (and `status`'s starting value) independent of whether
 * that chunk has loaded yet: `env.isSupabaseConfigured` is a plain string
 * check with no supabase-js dependency, so it is exactly equivalent to the
 * old `supabase !== null` test without requiring the client itself.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { setLanguage } = useI18n();
  const [status, setStatus] = useState<AuthStatus>(env.isSupabaseConfigured ? 'loading' : 'unconfigured');
  const [session, setSession] = useState<Session | null>(null);
  const [access, setAccess] = useState<AccessProfile | null>(null);
  const appliedLanguageFor = useRef<string | null>(null);

  const loadAccess = useCallback(async (): Promise<AccessProfile | null> => {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data, error } = await supabase.rpc('get_my_access');
    if (error) {
      setAccess(null);
      return null;
    }
    const parsed = parseAccessProfile(data);
    setAccess(parsed);
    return parsed;
  }, []);

  useEffect(() => {
    if (!env.isSupabaseConfigured) return;
    let active = true;
    let unsubscribe: (() => void) | undefined;

    const apply = async (next: Session | null) => {
      if (!active) return;
      setSession(next);
      if (!next) {
        safeStorage.remove(LANGUAGE_APPLIED_KEY);
        setAccess(null);
        setStatus('signed_out');
        return;
      }
      const profile = await loadAccess();
      if (!active) return;
      setStatus(profile ? 'signed_in' : 'signed_out');
      if (profile && appliedLanguageFor.current !== profile.userId) {
        appliedLanguageFor.current = profile.userId;
        // Apply the stored language preference once per sign-in, not on every
        // page load, so a language chosen with the switcher survives a reload.
        if (safeStorage.get(LANGUAGE_APPLIED_KEY) !== profile.userId) {
          safeStorage.set(LANGUAGE_APPLIED_KEY, profile.userId);
          setLanguage(profile.preferredLanguage);
        }
        void linkAttributionSession();
      }
    };

    void (async () => {
      const supabase = await getSupabase();
      if (!active || !supabase) return;
      void supabase.auth.getSession().then(({ data }) => apply(data.session));
      const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
        // Defer to avoid calling Supabase inside the auth callback (supabase-js guidance).
        setTimeout(() => void apply(next), 0);
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    })();

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [loadAccess, setLanguage]);

  const signIn = useCallback<AuthValue['signIn']>(async (email, password) => {
    const supabase = await getSupabase();
    if (!supabase) return { error: 'unconfigured' };
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (!error) return { error: null };
    if (/confirm/i.test(error.message)) return { error: 'unverified' };
    if (error.status === 429) return { error: 'rate_limited' };
    return { error: 'invalid' };
  }, []);

  const signUp = useCallback<AuthValue['signUp']>(async (input: SignUpInput) => {
    const supabase = await getSupabase();
    if (!supabase) return { error: 'unconfigured', needsVerification: false };
    const { data, error } = await supabase.auth.signUp({
      email: input.email.trim().toLowerCase(),
      password: input.password,
      options: {
        emailRedirectTo: `${env.appUrl}/auth/callback`,
        // User-editable metadata: used ONLY to create the profile/customer
        // record. It can never grant a role (see private.handle_new_auth_user).
        data: {
          full_name: input.fullName.trim(),
          mobile: input.mobileE164,
          preferred_language: input.preferredLanguage,
        },
      },
    });
    if (error) return { error: /registered|exists/i.test(error.message) ? 'exists' : 'failed', needsVerification: false };
    return { error: null, needsVerification: !data.session };
  }, []);

  const signOut = useCallback(async () => {
    const supabase = await getSupabase();
    if (!supabase) return;
    await supabase.auth.signOut();
    setAccess(null);
    setSession(null);
    setStatus('signed_out');
  }, []);

  const requestPasswordReset = useCallback<AuthValue['requestPasswordReset']>(async (email) => {
    const supabase = await getSupabase();
    if (!supabase) return { error: 'unconfigured' };
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${env.appUrl}/auth/reset-password`,
    });
    // Always report success to the UI so the form cannot be used to probe accounts.
    return { error: error && /rate/i.test(error.message) ? 'rate_limited' : null };
  }, []);

  const resendVerification = useCallback<AuthValue['resendVerification']>(async (email) => {
    const supabase = await getSupabase();
    if (!supabase) return { error: 'unconfigured' };
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim().toLowerCase(),
      options: { emailRedirectTo: `${env.appUrl}/auth/callback` },
    });
    // Only rate limiting is surfaced, so the form cannot be used to probe accounts.
    return { error: error?.status === 429 ? 'rate_limited' : null };
  }, []);

  const updatePassword = useCallback<AuthValue['updatePassword']>(async (password) => {
    const supabase = await getSupabase();
    if (!supabase) return { error: 'unconfigured' };
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) return { error: null };
    // Supabase Auth requires a recent sign-in to change a password (secure_password_change).
    if (error.code === 'reauthentication_needed') return { error: 'reauthenticate' };
    if (error.code === 'same_password') return { error: 'same_password' };
    return { error: 'failed' };
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      session,
      access,
      signIn,
      signUp,
      signOut,
      requestPasswordReset,
      resendVerification,
      updatePassword,
      refreshAccess: loadAccess,
    }),
    [status, session, access, signIn, signUp, signOut, requestPasswordReset, resendVerification, updatePassword, loadAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
